import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Container,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Checkbox,
  FormControlLabel,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useAuth } from "./contexts/AuthContext";
import CustomLogo from "./images/Daina-Login-Logo.png";
import PrivacyPolicy from "./Privacy";
import Terms from "./Terms";

const Login = () => {
  const [showReset, setShowReset] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [step, setStep] = useState("email"); // 'email' or 'code'
  const [errors, setErrors] = useState({ email: "", password: "", code: "", general: "" });
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);
  const [verificationToken, setVerificationToken] = useState("");

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const { login, loginWithGoogle, loading, sendVerificationCode, verifyCode } = useAuth();
  const googleBtnRef = useRef(null);

  useEffect(() => {
    /* global google */
    const loadGoogleScript = () => {
      const script = document.createElement("script");
      script.src = "https://accounts.google.com/gsi/client";
      script.async = true;
      script.defer = true;
      script.onload = initializeGoogle;
      document.body.appendChild(script);
    };

    const initializeGoogle = () => {
      if (window.google) {
        window.google.accounts.id.initialize({
          client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
          callback: handleGoogleResponse,
        });

        const renderGoogleButton = () => {
          if (googleBtnRef.current && window.google) {
            const availableWidth = googleBtnRef.current.offsetWidth || (window.innerWidth < 400 ? window.innerWidth - 64 : 350);
            window.google.accounts.id.renderButton(googleBtnRef.current, {
              type: "standard",
              theme: "outline",
              size: "large",
              text: "continue_with",
              width: availableWidth,
            });
          }
        };

        renderGoogleButton();
        setTimeout(renderGoogleButton, 500);
      }
    };

    loadGoogleScript();

    return () => {
      const script = document.querySelector('script[src="https://accounts.google.com/gsi/client"]');
      if (script) {
        document.body.removeChild(script);
      }
    };
  }, []);

  const handleGoogleResponse = async (response) => {
    try {
      const result = await loginWithGoogle(response.credential);
      if (result?.user && result?.access_token) {
        navigate("/");
      }
    } catch (err) {
      console.error("Google login error:", err.message);
      setErrors((prev) => ({
        ...prev,
        general: err.message || "Google login failed.",
      }));
    }
  };

  const handleSubmitReset = () => {
    setShowSuccess(true);
    setShowReset(false);
    navigate("/login");
  };

  const handleCancelClick = () => {
    setShowSuccess(false);
    setShowReset(false);
    navigate("/login");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (step === "email") {
      setErrors({ email: "", password: "", code: "", general: "" });

      if (!email) {
        setErrors((prev) => ({ ...prev, email: "Email is required." }));
        return;
      } else if (!/\S+@\S+\.\S+/.test(email)) {
        setErrors((prev) => ({ ...prev, email: "Invalid email address." }));
        return;
      }

      try {
        const result = await sendVerificationCode(email);
        if (result?.verification_token) {
          setVerificationToken(result.verification_token);
        }
        setStep("code");
      } catch (err) {
        setErrors((prev) => ({ ...prev, email: err.message || "Failed to send code." }));
      }
    } else {
      // Step is 'code'
      if (!code || code.length !== 6) {
        setErrors((prev) => ({ ...prev, code: "Please enter a valid 6-digit code." }));
        return;
      }

      try {
        const result = await verifyCode(email, code, verificationToken);
        if (result?.success) {
          // If verification is successful, we navigate to home
          navigate("/");
        } else {
          throw new Error("Invalid verification code.");
        }
      } catch (err) {
        setErrors((prev) => ({ ...prev, code: err.message || "Verification failed." }));
      }
    }
  };

  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      minHeight: '100vh',
      backgroundColor: '#f6f6f6',
      justifyContent: 'center',
      alignItems: 'center'
    }}>
      <Container maxWidth="xs" disableGutters sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
        <Paper elevation={0} sx={{
          p: { xs: 3, md: 3 },
          borderRadius: { xs: 0, md: '8px' },
          boxShadow: { xs: 'none', md: '0 4px 12px rgba(0,0,0,0.05)' },
          backgroundColor: '#fff',
          width: '100%',
          minHeight: { xs: '100vh', md: 'auto' },
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center'
        }}>
          <Box sx={{ mr: { xs: 5.5, sm: 0, md: 0 } }}>
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
              <Box component="img" src={CustomLogo} alt="DAINA Custom Logo" sx={{ width: { xs: 160, sm: 200 } }} />
            </Box>

            <Box
              component="form"
              onSubmit={handleSubmit}
              noValidate
              sx={{ display: "flex", flexDirection: "column" }}
            >
              {step === "email" ? (
                <>
                  {!showReset ? (
                    <>
                      <Typography variant="h5" sx={{ fontWeight: 600, fontSize: "16px", mb: 0.5, color: "#111", textAlign: 'left' }}>
                        Sign in
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1.2, color: "#555", textAlign: 'left', fontSize: "12px" }}>
                        Sign in or create an account
                      </Typography>

                      {/* <Button
                        variant="contained"
                        fullWidth
                        sx={{
                          bgcolor: "#5a31f4",
                          color: "#fff",
                          borderRadius: 0,
                          textTransform: "none",
                          fontWeight: 600,
                          py: 1.2,
                          mb: 1,
                          boxShadow: "none",
                          "&:hover": { bgcolor: "#4a24db", boxShadow: "none" }
                        }}
                      >
                        Continue with shop
                      </Button> */}

                      <Box sx={{ position: 'relative', width: '100%', mb: 1.5 }}>
                        <Button
                          className="custom-google-btn-inner"
                          variant="outlined"
                          fullWidth
                          sx={{
                            color: "#000",
                            borderColor: "#e0e0e0",
                            borderRadius: 0,
                            textTransform: "none",
                            fontWeight: 600,
                            py: 1,
                            minHeight: "44px",
                            pointerEvents: 'none',
                          }}
                        >
                          <Box component="img" src="https://upload.wikimedia.org/wikipedia/commons/c/c1/Google_%22G%22_logo.svg" sx={{ width: 18, height: 18, mr: 1.5 }} />
                          Continue with Google
                        </Button>

                        <Box
                          id="google-button-hidden"
                          ref={googleBtnRef}
                          sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            opacity: 0,
                            '& iframe': { width: '100% !important', height: '100% !important', pointerEvents: 'auto' }
                          }}
                        />
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                        <Box sx={{ flexGrow: 1, height: '1px', bgcolor: '#e0e0e0' }} />
                        <Typography sx={{ mx: 2, color: '#aaa', fontSize: '11px', mt: '-2px' }}>or</Typography>
                        <Box sx={{ flexGrow: 1, height: '1px', bgcolor: '#e0e0e0' }} />
                      </Box>
                    </>
                  ) : (
                    <>
                      <Typography variant="h5" sx={{ mb: 2, mt: 2, fontSize: isMobile ? 18 : 24 }}>
                        RESET YOUR PASSWORD
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 3, color: "#555", lineHeight: 1.6 }}>
                        We will send you an email to reset your password.
                      </Typography>
                    </>
                  )}

                  {showSuccess && (
                    <Box sx={{ backgroundColor: "#e9fbe7", border: "1px solid #4caf50", color: "#388e3c", p: 2, mb: 3, fontSize: "14px" }}>
                      We've sent you an email with a link to update your password.
                    </Box>
                  )}
                  {errors.general && (
                    <Box sx={{ backgroundColor: "#fdecea", border: "1px solid #f44336", color: "#d32f2f", p: 2, mb: 3, fontSize: "14px" }}>
                      {errors.general}
                    </Box>
                  )}

                  {!showReset && (
                    <TextField
                      variant="outlined"
                      placeholder="Email"
                      fullWidth
                      type="email"
                      required
                      size="small"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        if (errors.email) setErrors((prev) => ({ ...prev, email: "" }));
                      }}
                      error={!!errors.email}
                      helperText={errors.email || " "}
                      InputProps={{ style: { fontSize: '14px', borderRadius: 0, minHeight: '44px' } }}
                      sx={{
                        mb: 1,
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 0,
                          "&.Mui-focused fieldset": {
                            borderColor: "#000",
                          },
                        },
                        "& .MuiOutlinedInput-input": { padding: '12px 14px' }
                      }}
                    />
                  )}

                  {showReset ? (
                    <>
                      <Button
                        variant="contained"
                        sx={{ backgroundColor: "#000", color: "#fff", py: 1.2, fontSize: "14px", fontWeight: 500, width: "100px", alignSelf: "flex-start", borderRadius: 0, "&:hover": { backgroundColor: "#222" } }}
                        onClick={handleSubmitReset}
                      >
                        SUBMIT
                      </Button>
                      <Box sx={{ mt: 6 }}>
                        <Typography variant="body2" sx={{ cursor: "pointer" }} onClick={handleCancelClick}>
                          Cancel
                        </Typography>
                      </Box>
                    </>
                  ) : (
                    <>
                      <Button
                        type="submit"
                        variant="contained"
                        fullWidth
                        disabled={loading}
                        disableRipple
                        disableElevation
                        sx={{
                          backgroundColor: "#000",
                          color: "#fff",
                          borderRadius: 0,
                          py: 1.2,
                          mb: 2,
                          minHeight: "44px",
                          textTransform: 'none',
                          fontWeight: 600,
                          "&:hover": { backgroundColor: "#222" },
                          "&.Mui-disabled": {
                            backgroundColor: "#333",
                            color: "#fff",
                            opacity: 1
                          },
                        }}
                      >
                        {loading ? <CircularProgress size={18} sx={{ color: "white", position: "absolute" }} /> : null}
                        <span style={{ visibility: loading ? "hidden" : "visible" }}>Continue</span>
                      </Button>

                      <FormControlLabel
                        control={<Checkbox size="small" sx={{ color: '#000', py: 0, pl: 0, pr: 1, '&.Mui-checked': { color: '#000' } }} />}
                        label={<Typography variant="body2" sx={{ fontSize: '14px', fontWeight: 500, color: '#111', lineHeight: 1.3 }}>Subscribe now and be first to hear about new products, exclusive collaborations and discounts from DAINA.</Typography>}
                        sx={{ m: 0, mb: 1, alignItems: 'flex-start' }}
                      />
                      <Typography variant="body2" sx={{ fontSize: '12px', color: '#666', textAlign: 'center', mt: 0 }}>
                        By continuing, you agree to our <span onClick={() => setTermsOpen(true)} style={{ textDecoration: 'underline', cursor: 'pointer' }}>Terms of service</span>
                      </Typography>
                    </>
                  )}
                </>
              ) : (
                <>
                  <Typography variant="h5" sx={{ fontWeight: 600, fontSize: "20px", mb: 1, color: "#111", textAlign: 'left' }}>
                    Enter code
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 3, color: "#555", textAlign: 'left', fontSize: "14px" }}>
                    Sent to {email}
                  </Typography>

                  <TextField
                    variant="outlined"
                    placeholder="6-digit code"
                    fullWidth
                    required
                    size="small"
                    value={code}
                    onChange={(e) => {
                      setCode(e.target.value);
                      if (errors.code) setErrors((prev) => ({ ...prev, code: "" }));
                    }}
                    error={!!errors.code}
                    helperText={errors.code || " "}
                    InputProps={{ style: { fontSize: '14px', borderRadius: 0, minHeight: '44px' } }}
                    sx={{
                      mb: 1.5,
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 0,
                        borderColor: '#111',
                        "&.Mui-focused fieldset": {
                          borderColor: "#000",
                        },
                        "& fieldset": { borderWidth: '1.5px' }
                      },
                      "& .MuiOutlinedInput-input": { padding: '12px 14px' }
                    }}
                  />

                  <Button
                    type="submit"
                    variant="contained"
                    fullWidth
                    disabled={loading}
                    disableRipple
                    disableElevation
                    sx={{
                      backgroundColor: "#111",
                      color: "#fff",
                      borderRadius: 0,
                      py: 1,
                      mb: 4,
                      minHeight: "44px",
                      textTransform: 'none',
                      fontWeight: 700,
                      fontSize: '16px',
                      "&:hover": { backgroundColor: "#000" },
                      "&.Mui-disabled": {
                        backgroundColor: "#333",
                        color: "#fff",
                        opacity: 1
                      },
                    }}
                  >
                    {loading ? (
                      <CircularProgress
                        size={18}
                        sx={{ color: "white", position: "absolute" }}
                      />
                    ) : null}
                    <span style={{ visibility: loading ? "hidden" : "visible" }}>
                      Submit
                    </span>
                  </Button>

                  <Typography
                    onClick={() => setStep("email")}
                    sx={{ fontSize: '14px', color: '#111', cursor: 'pointer', textAlign: 'left', fontWeight: 500 }}
                  >
                    Sign in with a different email
                  </Typography>
                </>
              )}

              {isMobile && (
                <Box sx={{ textAlign: 'center', mt: { xs: 8, sm: 'auto' }, mb: 4 }}>
                  <Typography
                    onClick={() => setPrivacyOpen(true)}
                    sx={{ textDecoration: 'none', color: '#555', fontSize: '14px', cursor: 'pointer' }}
                  >
                    Privacy policy
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </Paper>

        {!isMobile && (
          <Box sx={{ position: 'absolute', bottom: 16, left: 0, width: '100%', textAlign: 'center' }}>
            <Typography
              onClick={() => setPrivacyOpen(true)}
              sx={{ textDecoration: 'none', color: '#555', fontSize: '14px', cursor: 'pointer', ml: 4 }}
            >
              Privacy policy
            </Typography>
          </Box>
        )}

        {/* Privacy Modal */}
        <Dialog open={privacyOpen} onClose={() => setPrivacyOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ m: 0, px: 3, pt: 3, display: 'flex', justifyContent: 'space-between', fontWeight: 600, fontSize: '28px' }}>
            Privacy policy
            <Box onClick={() => setPrivacyOpen(false)} sx={{ cursor: 'pointer', color: '#999', fontSize: '18px' }}>✕</Box>
          </DialogTitle>
          <DialogContent sx={{ p: 0, px: 3, pt: 0 }}>
            <PrivacyPolicy isModal={true} />
          </DialogContent>
        </Dialog>

        {/* Terms Modal */}
        <Dialog open={termsOpen} onClose={() => setTermsOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ m: 0, px: 3, pt: 3, display: 'flex', justifyContent: 'space-between', fontWeight: 600, fontSize: '28px' }}>
            Terms of service
            <Box onClick={() => setTermsOpen(false)} sx={{ cursor: 'pointer', color: '#999', fontSize: '18px' }}>✕</Box>
          </DialogTitle>
          <DialogContent sx={{ p: 0, px: 3, pt: 0 }}>
            <Terms isModal={true} />
          </DialogContent>
        </Dialog>
      </Container>
    </Box>
  );
};

export default Login;
